import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import locationPoi from '../../assets/icons/signage-plus/location-poi.svg';
import cross from '../../assets/icons/signage-plus/cross.svg';
import { ARCGIS_API, SIGNAGE_MEDIA } from '../../utils/constants';
import API from '../../utils/API';

const Weather = ({
  setSuccess,
  closeModal,
  refreshData,
  data = [],
  weatherData = {},
  zone = "",
  setZone,
  setZoneData,
  updatePlaylistsMedia,
  markUnsavedChanges
}) => {

  const [modalError, setModalError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [city, setCity] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentCities, setRecentCities] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('recentCities') || '[]');
    } catch {
      return [];
    }
  });
  const [selectedLocation, setSelectedLocation] = useState(undefined);

  /**
    * Reset modal
    */
  function resetModal() {
    setSubmitted(false);
    setModalError('');
    if (setZone)
      setZone('');
  }
  useEffect(() => {
    if (!selectedLocation || (selectedLocation && (!selectedLocation.x || !selectedLocation.y))) {
      setModalError(<FormattedMessage id="SELECT_CITY_INCORRECT" />);
    } else {
      setModalError('');
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (Object.keys(data).length > 0 && data.id) {
      const weatherData = data.content.paramters;
      setCity(weatherData.city || '');
      setSelectedLocation({ x: weatherData.lon, y: weatherData.lat });
    }
  }, [data]);

  useEffect(() => {
    if (zone !== SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET1 && zone !== SIGNAGE_MEDIA.CREATE_SIGNAGE.WIDGET2) return;
    if (weatherData && weatherData.content && weatherData.content.paramters) {
      const weatherParameters = weatherData.content.paramters;
      setCity(weatherParameters.city || '');
      setSelectedLocation({ x: weatherParameters.lon, y: weatherParameters.lat });
    }
  }, [weatherData, zone]);

  const showModal = () => {
    if (weatherData && weatherData.content && weatherData.content.paramters) {
      const weatherParameters = weatherData.content.paramters;
      setCity(weatherParameters.city || '');
      setSelectedLocation({ x: weatherParameters.lon, y: weatherParameters.lat });
    }
  };

  useEffect(() => {
    const modalElement = document.getElementById('weatherModal');
    modalElement.addEventListener('hidden.bs.modal', resetModal);
    //modalElement.addEventListener('shown.bs.modal', showModal);


    return () => {
      modalElement.removeEventListener('hidden.bs.modal', resetModal);
      //modalElement.removeEventListener('shown.bs.modal', showModal);
    };
  }, []);

  const handleAdd = async () => {
    try {
      if (zone) {
        setZoneData(prevWeatherData => {
          const newWeatherData = { ...prevWeatherData };
          newWeatherData[zone] = {
            zone: zone,
            content: {
              category: SIGNAGE_MEDIA.MEDIA_CATEGORY.WIDGET,
              subcategory: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER,
              type: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER,
              name: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER,
              paramters: {
                city: city,
                lat: selectedLocation.y,
                lon: selectedLocation.x,
              }
            }
          };
          return newWeatherData;
        });
      } else {
        const formData = {
          category: SIGNAGE_MEDIA.MEDIA_CATEGORY.WIDGET,
          subcategory: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER,
          type: SIGNAGE_MEDIA.MEDIA_SUB_CATEGORY.WEATHER,
          name: "",
          content: {
            paramters: {
              city: city,
              lat: selectedLocation.y,
              lon: selectedLocation.x,
            }
          }
        };
        if (Object.keys(data).length > 0 && data.id) {
          formData.id = data.id;
          await API.updateMedia(formData);
          updatePlaylistsMedia(formData);
          setSuccess(<FormattedMessage id="EDIT_WEATHER_SUCCESS" />);
        } else {
          await API.addMedia(formData);
          setSuccess(<FormattedMessage id="ADD_WEATHER_SUCCESS" />);
        }
        markUnsavedChanges && markUnsavedChanges();
        refreshData();
      }
      closeModal("weatherModal");
    } catch (err) {
      const errorId = Object.keys(data).length > 0 && data.id ? "EDIT_WEATHER_FAILED" : "ADD_WEATHER_FAILED";
      setModalError(<FormattedMessage id={errorId} values={{ errorMsg: <b>{err.message}</b> }} />);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!city) {
      setModalError(
        <>
          <FormattedMessage id="EMPTY_FIELD_ERROR" />
          <FormattedMessage id="SEARCH_CITY" />
        </>
      );
      return;
    }
    if (!selectedLocation || (selectedLocation && (!selectedLocation.x || !selectedLocation.y))) {
      setModalError(<FormattedMessage id="SELECT_CITY_INCORRECT" />);
      return;
    }
    handleAdd();
  };

  const handleCityInput = async (e) => {
    const value = e.target.value;
    setCity(value);

    if (!value) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!value) {
      setModalError(
        <>
          <FormattedMessage id="EMPTY_FIELD_ERROR" />
          <FormattedMessage id="SEARCH_CITY" />
        </>
      );
    } 

    setShowSuggestions(true);

    const url = `${ARCGIS_API.suggest}?text=${encodeURIComponent(value)}&maxSuggestions=10&countryCode=US&f=json`;

    try {
      const res = await fetch(url);
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch (err) {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    setCity(suggestion.text);
    setSuggestions([]);
    setShowSuggestions(false);
    let location = null;
    try {
      const magicKey = suggestion.magicKey;
      if (magicKey) {
        const geocodeUrl = `${ARCGIS_API.findAddressCandidates}?magicKey=${encodeURIComponent(magicKey)}&f=json`;
        const res = await fetch(geocodeUrl);
        const data = await res.json();
        if (data.candidates && data.candidates.length > 0 && data.candidates[0].location) {
          location = data.candidates[0].location;
        }
      }
      if (!location && suggestion.location) {
        location = suggestion.location;
      }
    } catch {
      if (suggestion.location) {
        location = suggestion.location;
      }
    }

    setRecentCities(prev => {
      const updated = [
        { name: suggestion.text, location: location || null }
      ];
      prev.forEach(c => {
        let cityName, prevLocation;
        if (typeof c === 'object') {
          cityName = c.name;
          prevLocation = c.location !== undefined ? c.location : null;
        } else {
          cityName = c;
          prevLocation = null;
        }
        if (cityName !== suggestion.text) {
          updated.push({ name: cityName, location: prevLocation });
        }
      });
      localStorage.setItem('recentCities', JSON.stringify(updated));
      return updated;
    });

    setSelectedLocation(location || undefined);
  };

  const handleRecentCityClick = (cityObj) => {
    setCity(cityObj.name);
    setShowSuggestions(false);
    if (cityObj.location) {
      setSelectedLocation(cityObj.location);
    } else {
      setSelectedLocation(undefined);
    }
  };

  const handleRemoveRecent = (cityName) => {
    setRecentCities(prev => {
      const updated = prev.filter(c => {
        if (typeof c === 'object') {
          return c.name !== cityName;
        }
        return c !== cityName;
      });
      localStorage.setItem('recentCities', JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    function handleClickOutside(e) {
      const input = document.getElementById('weather-city-input');
      const dropdown = document.getElementById('weather-suggest-dropdown');
      if (
        input &&
        !input.contains(e.target) &&
        dropdown &&
        !dropdown.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    }
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  return (
    <div className='modal modal-mse fade' id='weatherModal' tabIndex='-1' aria-labelledby='weatherModalLabel' aria-hidden='true'>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content modal-mes-content'>
          <div className='modal-header modal-mes-header' style={{ backgroundColor: '#347BFA' }}>
            <h5 className='modal-title modal-mes-title'><FormattedMessage id='SIGNAGE_WEATHER' /></h5>
            <button id="closeModal" type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
          </div>
          <div className='modal-body' style={{ height: '300px' }}>
            <form onSubmit={handleSubmit}>
              <div className='row'>
                <div className="mb-3 col-12">
                  <label className="font-weight-600 font-size-14 mb-2" htmlFor="weather-city-input">
                    <FormattedMessage id="SEARCH_CITY" />:
                  </label>
                  <div className="btn-group col-12" style={{ position: 'relative' }}>
                    <input
                      id="weather-city-input"
                      type="text"
                      className={`form-control h-54 ${submitted && (modalError ? 'is-invalid' : 'is-valid')}`}
                      value={city}
                      aria-expanded="false"
                      onChange={handleCityInput}
                      autoComplete="off"
                      onFocus={() => {
                        setCity('');
                        setSelectedLocation(undefined);
                        setShowSuggestions(true);
                      }}
                    />
                    {suggestions.length > 0 && showSuggestions && (
                      <ul
                        id="weather-suggest-dropdown"
                        className="dropdown-menu show"
                        style={{
                          top: '100%',
                          left: 0,
                          width: '100%',
                          maxHeight: 180,
                          overflowY: 'auto'
                        }}
                      >
                        {suggestions.map(s => (
                          <li key={s.magicKey}>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => {
                                handleSuggestionClick(s);
                                setShowSuggestions(false);
                              }}
                            >
                              {s.text}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <div className='row'>
                <label className="font-weight-600 font-size-14"><FormattedMessage id="RECENT" />:</label>
                {recentCities.length > 0 && (
                  <div>
                    {recentCities.slice(0, 2).map((c) => {
                      const cityObj = typeof c === 'object' ? c : { name: c };
                      return (
                        <div
                          key={cityObj.name}
                          className="h-54 d-flex align-items-center"
                          style={{
                            borderBottom: '1px solid rgba(240, 243, 246, 1)',
                          }}
                          onClick={() => handleRecentCityClick(cityObj)}
                        >
                          <img src={locationPoi} alt="" className='icon-24 me-2' />
                          <span style={{ flex: 1, textAlign: 'left', fontSize: 15, color: '#222' }}>{cityObj.name}</span>
                          <img
                            src={cross}
                            alt="remove"
                            className='icon-24'
                            onClick={e => {
                              e.stopPropagation();
                              handleRemoveRecent(cityObj.name);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {submitted && modalError && (
                <div className="modal-error invalid-feedback d-block fw-bold text-center">
                  {modalError}
                </div>
              )}
            </form>
          </div>
          <div className='modal-footer modal-mes-footer d-flex align-items-center'>
            <button id="backGroupModalBtn" type='button' className='btn btn-outline-secondary me-3' data-bs-dismiss='modal'>
              <FormattedMessage id='CANCEL' />
            </button>
            <button id="saveGroupBtn" className='btn btn-primary but-darkblue' type='button' onClick={handleSubmit}>
              <FormattedMessage id='ADD_WIDGET' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Weather;
